'use client'

import { LocationType } from '@/app/(dashboard)/projects/FolderSelectionTree'
import { createContext, useContext, useState, ReactNode } from 'react'

type Assignment = {
  type: 'checklist' | 'playbook'
  uuid: string
  userUuid: string
}

export type StepsData = {
  step1: {
    file: File | undefined
    location: LocationType | undefined
  }
  step2: {
    documentTypeUuid: string | undefined
    checklists: string[]
    playbooks: string[]
    assignments: Assignment[]
  }
}

interface ModalContextType {
  currentStep: number
  stepsData: StepsData
  nextStep: () => void
  prevStep: () => void
  updateStepsData: (
    stepKey: keyof StepsData,
    data: StepsData[keyof StepsData]
  ) => void
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

const initialStepsData: StepsData = {
  step1: {
    file: undefined,
    location: undefined,
  },
  step2: {
    documentTypeUuid: undefined,
    checklists: [],
    playbooks: [],
    assignments: [],
  },
}

export function UploadCrunchProvider({ children }: { children: ReactNode }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [stepsData, setStepsData] = useState<StepsData>(initialStepsData)

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 3))
  }

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const updateStepsData = (
    stepKey: keyof StepsData,
    data: StepsData[keyof StepsData]
  ) => {
    setStepsData((prev) => ({
      ...prev,
      [stepKey]: data,
    }))
  }

  return (
    <ModalContext.Provider
      value={{
        currentStep,
        stepsData,
        nextStep,
        prevStep,
        updateStepsData,
      }}
    >
      {children}
    </ModalContext.Provider>
  )
}

export function useUploadCrunch() {
  const context = useContext(ModalContext)

  if (context === undefined) {
    throw new Error(
      'useUploadCrunch must be used within a UploadCrunchProvider'
    )
  }

  return context
}
